import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Power,
  Sun,
  Droplet,
  Lock,
  Unlock,
  Camera,
  Wind,
  Thermometer,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import smartThingsService from '../services/smartThingsService';
import { IoTDevice, DEVICE_TYPES, getDeviceControls } from '../models/iotModels';
import './RoomControls.css';

const RoomControls = ({ userRoomId }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedDevices, setExpandedDevices] = useState(new Set());

  // Device state controls
  const [deviceStates, setDeviceStates] = useState({});

  useEffect(() => {
    loadRoomDevices();
  }, [userRoomId]);

  const loadRoomDevices = async () => {
    setLoading(true);
    try {
      const allDevices = await smartThingsService.getAllDevices();
      
      // Filter devices for the user's room
      const roomDevices = allDevices.filter(d => d.roomId === userRoomId);
      
      // Fetch status for each device
      const devicesWithStatus = await Promise.all(
        roomDevices.map(async (device) => {
          try {
            const status = await smartThingsService.getDeviceStatus(device.deviceId);
            const deviceInstance = new IoTDevice({ ...device, state: status });
            
            // Initialize device state in local state
            setDeviceStates(prev => ({
              ...prev,
              [device.deviceId]: extractDeviceState(deviceInstance)
            }));
            
            return deviceInstance;
          } catch (error) {
            console.error(`Failed to load status for device ${device.deviceId}:`, error);
            return new IoTDevice(device);
          }
        })
      );
      
      setDevices(devicesWithStatus);
    } catch (error) {
      toast.error('Failed to load room devices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshDevices = async () => {
    setRefreshing(true);
    await loadRoomDevices();
    setRefreshing(false);
    toast.success('Devices refreshed');
  };

  const extractDeviceState = (device) => {
    const state = {};
    
    if (device.hasCapability('switch')) {
      state.switch = device.getState('switch', 'switch') === 'on';
    }
    
    if (device.hasCapability('switchLevel')) {
      state.level = parseInt(device.getState('switchLevel', 'level')) || 0;
    }
    
    if (device.hasCapability('colorControl')) {
      state.hue = parseInt(device.getState('colorControl', 'hue')) || 0;
      state.saturation = parseInt(device.getState('colorControl', 'saturation')) || 0;
    }
    
    if (device.hasCapability('colorTemperature')) {
      state.colorTemperature = parseInt(device.getState('colorTemperature', 'colorTemperature')) || 2700;
    }
    
    if (device.hasCapability('thermostatMode')) {
      state.thermostatMode = device.getState('thermostatMode', 'thermostatMode') || 'auto';
    }
    
    if (device.hasCapability('thermostatHeatingSetpoint')) {
      state.heatingSetpoint = parseInt(device.getState('thermostatHeatingSetpoint', 'heatingSetpoint')) || 20;
    }
    
    if (device.hasCapability('thermostatCoolingSetpoint')) {
      state.coolingSetpoint = parseInt(device.getState('thermostatCoolingSetpoint', 'coolingSetpoint')) || 24;
    }
    
    if (device.hasCapability('lock')) {
      state.locked = device.getState('lock', 'lock') === 'locked';
    }
    
    if (device.hasCapability('windowShade')) {
      state.windowShade = device.getState('windowShade', 'windowShade') || 'unknown';
    }
    
    if (device.hasCapability('fanSpeed')) {
      state.fanSpeed = parseInt(device.getState('fanSpeed', 'fanSpeed')) || 0;
    }
    
    return state;
  };

  const toggleExpanded = (deviceId) => {
    setExpandedDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  };

  // Control handlers
  const handleToggleSwitch = async (device) => {
    const currentState = deviceStates[device.id]?.switch;
    const newState = !currentState;
    
    // Optimistic update
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], switch: newState }
    }));
    
    try {
      if (newState) {
        await smartThingsService.turnOnDevice(device.id);
        toast.success(`${device.name} turned on`);
      } else {
        await smartThingsService.turnOffDevice(device.id);
        toast.success(`${device.name} turned off`);
      }
    } catch (error) {
      // Revert on error
      setDeviceStates(prev => ({
        ...prev,
        [device.id]: { ...prev[device.id], switch: currentState }
      }));
      toast.error('Failed to toggle device: ' + error.message);
    }
  };

  const handleBrightnessChange = async (device, level) => {
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], level }
    }));
    
    try {
      await smartThingsService.setLightLevel(device.id, level);
    } catch (error) {
      toast.error('Failed to set brightness: ' + error.message);
    }
  };

  const handleColorChange = async (device, hue, saturation) => {
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], hue, saturation }
    }));
    
    try {
      await smartThingsService.executeDeviceCommand(
        device.id,
        'colorControl',
        'setColor',
        [{ hue, saturation }]
      );
      toast.success('Color changed');
    } catch (error) {
      toast.error('Failed to set color: ' + error.message);
    }
  };

  const handleThermostatMode = async (device, mode) => {
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], thermostatMode: mode }
    }));
    
    try {
      await smartThingsService.setThermostatMode(device.id, mode);
      toast.success(`Thermostat set to ${mode} mode`);
    } catch (error) {
      toast.error('Failed to set thermostat mode: ' + error.message);
    }
  };

  const handleTemperatureSetpoint = async (device, type, value) => {
    const stateKey = type === 'heating' ? 'heatingSetpoint' : 'coolingSetpoint';
    
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], [stateKey]: value }
    }));
    
    try {
      if (type === 'heating') {
        await smartThingsService.executeDeviceCommand(
          device.id,
          'thermostatHeatingSetpoint',
          'setHeatingSetpoint',
          [value]
        );
      } else {
        await smartThingsService.executeDeviceCommand(
          device.id,
          'thermostatCoolingSetpoint',
          'setCoolingSetpoint',
          [value]
        );
      }
      toast.success('Temperature setpoint updated');
    } catch (error) {
      toast.error('Failed to set temperature: ' + error.message);
    }
  };

  const handleLockToggle = async (device) => {
    const currentState = deviceStates[device.id]?.locked;
    const newState = !currentState;
    
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], locked: newState }
    }));
    
    try {
      if (newState) {
        await smartThingsService.lockDoor(device.id);
        toast.success(`${device.name} locked`);
      } else {
        await smartThingsService.unlockDoor(device.id);
        toast.success(`${device.name} unlocked`);
      }
    } catch (error) {
      setDeviceStates(prev => ({
        ...prev,
        [device.id]: { ...prev[device.id], locked: currentState }
      }));
      toast.error('Failed to toggle lock: ' + error.message);
    }
  };

  const handleShadeControl = async (device, command) => {
    try {
      await smartThingsService.executeDeviceCommand(
        device.id,
        'windowShade',
        command,
        []
      );
      toast.success(`Blinds ${command}`);
      setTimeout(loadRoomDevices, 1000);
    } catch (error) {
      toast.error('Failed to control blinds: ' + error.message);
    }
  };

  const handleFanSpeed = async (device, speed) => {
    setDeviceStates(prev => ({
      ...prev,
      [device.id]: { ...prev[device.id], fanSpeed: speed }
    }));
    
    try {
      await smartThingsService.executeDeviceCommand(
        device.id,
        'fanSpeed',
        'setFanSpeed',
        [speed]
      );
      toast.success('Fan speed changed');
    } catch (error) {
      toast.error('Failed to set fan speed: ' + error.message);
    }
  };

  const renderDeviceControls = (device) => {
    const state = deviceStates[device.id] || {};
    const controls = getDeviceControls(device.type, device.capabilities);
    const isExpanded = expandedDevices.has(device.id);

    return (
      <div className="device-controls">
        {/* Switch Control */}
        {controls.includes('switch') && (
          <div className="control-group">
            <label>Power</label>
            <button
              className={`toggle-btn ${state.switch ? 'active' : ''}`}
              onClick={() => handleToggleSwitch(device)}
              disabled={!device.isOnline()}
            >
              <Power size={18} />
              {state.switch ? 'ON' : 'OFF'}
            </button>
          </div>
        )}

        {isExpanded && (
          <>
            {/* Brightness Control */}
            {controls.includes('brightness') && state.switch && (
              <div className="control-group">
                <label>Brightness: {state.level}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={state.level || 0}
                  onChange={(e) => handleBrightnessChange(device, parseInt(e.target.value))}
                  className="slider"
                  disabled={!device.isOnline()}
                />
              </div>
            )}

            {/* Color Control */}
            {controls.includes('color') && state.switch && (
              <div className="control-group">
                <label>Color</label>
                <div className="color-controls">
                  <div>
                    <label className="sub-label">Hue: {state.hue}°</label>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={state.hue || 0}
                      onChange={(e) => handleColorChange(device, parseInt(e.target.value), state.saturation || 100)}
                      className="slider"
                      disabled={!device.isOnline()}
                    />
                  </div>
                  <div>
                    <label className="sub-label">Saturation: {state.saturation}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={state.saturation || 0}
                      onChange={(e) => handleColorChange(device, state.hue || 0, parseInt(e.target.value))}
                      className="slider"
                      disabled={!device.isOnline()}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Thermostat Controls */}
            {controls.includes('thermostat') && (
              <>
                <div className="control-group">
                  <label>Mode</label>
                  <div className="mode-buttons">
                    {['auto', 'heat', 'cool', 'off'].map(mode => (
                      <button
                        key={mode}
                        className={`mode-btn ${state.thermostatMode === mode ? 'active' : ''}`}
                        onClick={() => handleThermostatMode(device, mode)}
                        disabled={!device.isOnline()}
                      >
                        {mode.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="control-group">
                  <label>Heating Setpoint: {state.heatingSetpoint}°C</label>
                  <input
                    type="range"
                    min="15"
                    max="30"
                    value={state.heatingSetpoint || 20}
                    onChange={(e) => handleTemperatureSetpoint(device, 'heating', parseInt(e.target.value))}
                    className="slider"
                    disabled={!device.isOnline()}
                  />
                </div>
                <div className="control-group">
                  <label>Cooling Setpoint: {state.coolingSetpoint}°C</label>
                  <input
                    type="range"
                    min="18"
                    max="32"
                    value={state.coolingSetpoint || 24}
                    onChange={(e) => handleTemperatureSetpoint(device, 'cooling', parseInt(e.target.value))}
                    className="slider"
                    disabled={!device.isOnline()}
                  />
                </div>
              </>
            )}

            {/* Lock Control */}
            {controls.includes('lock') && (
              <div className="control-group">
                <button
                  className={`lock-btn ${state.locked ? 'locked' : 'unlocked'}`}
                  onClick={() => handleLockToggle(device)}
                  disabled={!device.isOnline()}
                >
                  {state.locked ? <Lock size={18} /> : <Unlock size={18} />}
                  {state.locked ? 'LOCKED' : 'UNLOCKED'}
                </button>
              </div>
            )}

            {/* Blinds Control */}
            {controls.includes('blinds') && (
              <div className="control-group">
                <label>Window Shade</label>
                <div className="shade-buttons">
                  <button
                    className="shade-btn"
                    onClick={() => handleShadeControl(device, 'open')}
                    disabled={!device.isOnline()}
                  >
                    Open
                  </button>
                  <button
                    className="shade-btn"
                    onClick={() => handleShadeControl(device, 'close')}
                    disabled={!device.isOnline()}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Fan Speed Control */}
            {controls.includes('fanSpeed') && (
              <div className="control-group">
                <label>Fan Speed: {state.fanSpeed}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={state.fanSpeed || 0}
                  onChange={(e) => handleFanSpeed(device, parseInt(e.target.value))}
                  className="slider"
                  disabled={!device.isOnline()}
                />
              </div>
            )}
          </>
        )}

        {/* Expand/Collapse Button */}
        {(controls.length > 1 || (controls.includes('switch') && controls.length > 1)) && (
          <button
            className="expand-btn"
            onClick={() => toggleExpanded(device.id)}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isExpanded ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="room-controls-loading">
        <div className="spinner-large"></div>
        <p>Loading your room devices...</p>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="room-controls-empty">
        <AlertCircle size={48} color="#94a3b8" />
        <h2>No Devices Found</h2>
        <p>There are no smart devices assigned to your room yet.</p>
        <p>Please contact the front desk for assistance.</p>
      </div>
    );
  }

  return (
    <div className="room-controls-container">
      <div className="room-controls-header">
        <div className="header-left">
          <h1>🏠 Your Room Controls</h1>
          <p className="subtitle">Manage your smart devices with ease</p>
        </div>
        <button className="btn-refresh" onClick={refreshDevices} disabled={refreshing}>
          <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      <div className="devices-overview">
        <div className="overview-card">
          <CheckCircle size={24} color="#10b981" />
          <div>
            <div className="overview-value">{devices.filter(d => d.isOnline()).length}</div>
            <div className="overview-label">Online</div>
          </div>
        </div>
        <div className="overview-card">
          <AlertCircle size={24} color="#ef4444" />
          <div>
            <div className="overview-value">{devices.filter(d => !d.isOnline()).length}</div>
            <div className="overview-label">Offline</div>
          </div>
        </div>
      </div>

      <div className="devices-list">
        {devices.map(device => (
          <div key={device.id} className={`device-control-card ${device.isOnline() ? 'online' : 'offline'}`}>
            <div className="device-header">
              <div className="device-title">
                <span className="device-icon-large">{device.getIcon()}</span>
                <div>
                  <h3>{device.name}</h3>
                  <p className="device-type-label">{device.getTypeLabel()}</p>
                </div>
              </div>
              <span className={`status-badge ${device.status}`}>
                {device.isOnline() ? 'Online' : 'Offline'}
              </span>
            </div>

            {device.isOnline() ? (
              renderDeviceControls(device)
            ) : (
              <div className="device-offline-message">
                <AlertCircle size={18} />
                <span>This device is currently offline</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoomControls;
